import moment from 'moment-timezone';

export type TimeRange = {
  id: string;
  dayOfWeek: number; // 0 = Sunday, 6 = Saturday
  startTime: string; // Format: "HH:mm"
  endTime: string; // Format: "HH:mm"
};

export type Schedule = {
  id: string;
  startDate: string; // ISO datetime string
  endDate?: string | null; // ISO datetime string
  timeRanges?: TimeRange[] | null;
};

export function isScheduleActiveNow(schedule: Schedule | null | undefined, timezone: string): boolean {
  if (!schedule) {
    return true;
  }

  const now = moment.tz(timezone);

  // Check date range boundaries
  if (schedule.startDate) {
    const startMoment = moment.utc(schedule.startDate).tz(timezone);
    if (now.isBefore(startMoment)) {
      return false;
    }
  }

  if (schedule.endDate) {
    const endMoment = moment.utc(schedule.endDate).tz(timezone).endOf('day');
    if (now.isAfter(endMoment)) {
      return false;
    }
  }

  // If no time ranges, schedule is active all day within date range
  if (!schedule.timeRanges || schedule.timeRanges.length === 0) {
    return true;
  }

  // Check if current day/time matches any time range
  const currentDay = now.day(); // 0 = Sunday, 6 = Saturday
  const currentTime = now.format('HH:mm');

  return schedule.timeRanges.some((timeRange) => {
    const { dayOfWeek, startTime, endTime } = timeRange;

    if (dayOfWeek !== currentDay) {
      return false;
    }

    // Handle time ranges that don't span midnight
    if (startTime <= endTime) {
      return currentTime >= startTime && currentTime <= endTime;
    }

    // Handle time ranges that span midnight (e.g., 22:00 to 06:00)
    return currentTime >= startTime || currentTime <= endTime;
  });
}

export function getNextScheduleChangeTime(schedules: (Schedule | null | undefined)[], timezone: string): number | null {
  const now = moment.tz(timezone);
  const candidates: moment.Moment[] = [];

  // Add 1 second minimum gap to prevent boundary thrashing when timeout fires exactly at change time
  // This ensures we don't repeatedly schedule timeouts for boundaries we've just passed
  const minGapSeconds = 1;
  const nowWithGap = now.clone().add(minGapSeconds, 'seconds');

  schedules.forEach((schedule) => {
    if (!schedule) {
      return;
    }

    // Check start date boundary
    if (schedule.startDate) {
      const startMoment = moment.utc(schedule.startDate).tz(timezone);
      if (startMoment.isAfter(nowWithGap)) {
        candidates.push(startMoment);
      }
    }

    // Check end date boundary (end of day)
    if (schedule.endDate) {
      const endMoment = moment.utc(schedule.endDate).tz(timezone).endOf('day').add(1, 'second').millisecond(0);
      if (endMoment.isAfter(nowWithGap)) {
        candidates.push(endMoment);
      }
    }

    // Check time range boundaries
    if (schedule.timeRanges && schedule.timeRanges.length > 0) {
      // Look ahead up to 7 days to find next time range change
      schedule.timeRanges.forEach((timeRange) => {
        const { dayOfWeek, startTime, endTime } = timeRange;

        // Find next occurrence of this day of week
        const daysUntilTarget = (dayOfWeek - now.day() + 7) % 7;
        const targetDate = now.clone().add(daysUntilTarget, 'days');

        // Add start time boundary
        const [startHour, startMinute] = startTime.split(':').map(Number);
        const startMoment = targetDate.clone().hour(startHour).minute(startMinute).second(0);
        if (startMoment.isAfter(nowWithGap)) {
          candidates.push(startMoment);
        }

        // Add end time boundary (1 minute after end time, since times are compared as HH:mm strings)
        // If endTime is "13:00", item is active during 13:00:00-13:00:59, becomes inactive at 13:01:00
        const [endHour, endMinute] = endTime.split(':').map(Number);
        const endMoment = targetDate.clone().hour(endHour).minute(endMinute).second(0).add(1, 'minute');
        if (endMoment.isAfter(nowWithGap)) {
          candidates.push(endMoment);
        }

        // If we're on the target day but after the time range, check next week too
        if (daysUntilTarget === 0) {
          const nextWeekDate = targetDate.clone().add(7, 'days');

          const nextWeekStart = nextWeekDate.clone().hour(startHour).minute(startMinute).second(0);
          candidates.push(nextWeekStart);

          const nextWeekEnd = nextWeekDate.clone().hour(endHour).minute(endMinute).second(0).add(1, 'minute');
          candidates.push(nextWeekEnd);
        }
      });
    }
  });

  if (candidates.length === 0) {
    return null;
  }

  // Find the earliest next change time
  const nextChange = moment.min(candidates);
  return nextChange.valueOf();
}
